import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import Linkify from 'linkify-react';
import HtmlContent from 'components/HtmlContent';
import MediaViewer from 'components/MediaViewer';
import { sanitizePageContent } from 'services/sanitizationService';
import { linkifyOptions } from './linkifyOptions';

const Body = styled('div')`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const Main = styled('div')`
  flex: 1;
  min-width: 0;
`;

const Side = styled('div')`
  flex: 0 0 300px;

  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 600px) {
    flex-basis: auto;
    width: 100%;
    order: -1;
  }
`;

const Title = styled('h2')`
  margin: 0 0 0.5rem;
`;

const Description = styled('div')`
  color: #666;
  margin-bottom: 1rem;
  white-space: pre-line;
`;

const Fields = styled('table')`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`;

const FieldRow = styled('tr')`
  border-bottom: 1px solid #eee;

  &:last-of-type {
    border-bottom: none;
  }
`;

const FieldKey = styled('td')`
  padding: 0.5rem 0.75rem 0.5rem 0;
  vertical-align: top;

  color: #888;
`;

const FieldValue = styled('td')`
  padding: 0.5rem 0;
  vertical-align: top;
  text-align: right;
  word-break: break-word;

  a {
    color: #15c;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const renderFieldValue = (value) => <Linkify options={linkifyOptions}>{value ?? ''}</Linkify>;

// Attributes drive the order; an entry only shows the ones it has a value for.
const EntryView = ({ entry, attributes }) => {
  const fields = (attributes ?? [])
    .map((attribute) => ({
      id: attribute.id,
      name: attribute.name,
      value: (entry?.fields ?? []).find((field) => field.attributeId === attribute.id)?.value,
    }))
    .filter((field) => `${field.value ?? ''}`.trim());
  const hasMedia = entry?.media?.length > 0;

  return (
    <Body>
      <Main>
        <Title>{entry?.name}</Title>
        {entry?.description && <Description>{entry.description}</Description>}
        {entry?.content && (
          <HtmlContent dangerouslySetInnerHTML={{ __html: sanitizePageContent(entry.content) }} />
        )}
      </Main>

      {(hasMedia || fields.length > 0) && (
        <Side>
          {hasMedia && <MediaViewer key={entry.id} items={entry.media} />}
          {fields.length > 0 && (
            <Fields>
              <tbody>
                {fields.map((field) => (
                  <FieldRow key={field.id}>
                    <FieldKey>{field.name}</FieldKey>
                    <FieldValue data-user-content>{renderFieldValue(field.value)}</FieldValue>
                  </FieldRow>
                ))}
              </tbody>
            </Fields>
          )}
        </Side>
      )}
    </Body>
  );
};

EntryView.propTypes = {
  entry: PropTypes.object,
  attributes: PropTypes.array,
};

export default EntryView;
